import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Tabs from './components/Tabs';
import HomeNavbar from './components/HomeNavbar';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import Terms from './components/Terms';
import Find from './components/Find';
import Creators from './components/Creators';
import CreatorDetail from './components/CreatorDetail';
import Outreach from './components/Outreach';
import OutreachLogs from './components/OutreachLogs';
import CreatorsLanding from './components/CreatorsLanding';
import LoadingPage from './components/LoadingPage';
import PriceNegotiation from './components/PriceNegotiation';
import CollaborationCreator from './components/CollaborationCreator';
import Collaborations from './components/Collaborations';
import CollaborationDetail from './components/CollaborationDetail';
import ThankYou from './components/ThankYou';
import SignUp from './components/SignUp';
import SignIn from './components/SignIn';
import EmailVerification from './components/EmailVerification';
import LoginVerification from './components/LoginVerification';
import CreatorOffer from './components/CreatorOffer';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './context/AuthContext';
import Stats from './components/Stats';
import './styles/compat-stats.css';
import Stripe from './components/Stripe';
import Batch from './components/Batch';
import BatchProposal from './components/BatchProposal';
import BatchCreator from './components/BatchCreator';
import BatchDetail from './components/BatchDetail';
import ManualBatchRegistration from './components/ManualBatchRegistration';
import Transcript from './components/Transcript';
import './styles/compat-base.css';
import './styles/compat-modal.css';
import './styles/compat-actions.css';
import CollaborationInquiry from './components/CollaborationInquiry';
import Email from './components/Email';
import Unsubscribe from './components/Unsubscribe';
import PaidAds from './components/PaidAds';
import './styles/compat-table.css';
import AffiliateStats from './components/AffiliateStats';
import AffiliateOverview from './components/AffiliateOverview';
import AffiliateAnalytics from './components/AffiliateAnalytics';
import SentEmails from './components/SentEmails';
import DraftReview from './components/DraftReview';
import FeedbackReport from './components/FeedbackReport';
import './App.css';

// 어필리에이트 route는 이제 DB 정본(config 폐지)이라 빌드타임에 목록을 알 수 없다. 그래서
// "어필리에이트 경로 화이트리스트" 대신 "비-어필리에이트 최상위 경로 목록"을 두고 반전 판정한다.
// 아래에 없는 1~2세그먼트 최상위 경로(예: /ben, /harrison, /harrison/payouts)면 어필리에이트로
// 간주해 full-screen 렌더. ⚠️ 새 최상위 라우트(비-어필리에이트)를 App에 추가하면 여기에도 넣을 것.
const NON_AFFILIATE_TOP = new Set([
  'batch', 'batch-creator', 'batch-proposal', 'collaboration-workflow', 'collaborations',
  'collaborations-creator', 'company', 'creator', 'creators', 'email',
  'find', 'inquiry', 'loading', 'outreach', 'paid-ads', 'price-negotiation',
  // 'recruit' is served by the Pages Function proxy (functions/recruit/[[path]].js),
  // never by this router — listed so a proxy miss 404s instead of being mistaken
  // for an affiliate route and rendering an empty affiliate dashboard.
  'recruit', 'sent-emails',
  'privacy', 'privacy-policy', 'signin', 'signup', 'stats', 'stripe', 'terms',
  'thank-you', 'transcript', 'unsubscribe', 'verify-email', 'verify-login'
]);

function AppContent() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const location = useLocation();

  // affiliate 페이지(개별 /{route} + 하위 탭 /{route}/payouts + 통합 /affiliates)는 full-screen.
  // 최상위 세그먼트가 NON_AFFILIATE_TOP에 없으면 어필리에이트로 간주. /affiliates는 명시 포함.
  const path = location.pathname.toLowerCase();
  const segs = path.split('/').filter(Boolean);
  const isAffiliatePath = path === '/affiliates' || path === '/sent-emails'
    || (segs.length >= 1 && segs.length <= 2 && !NON_AFFILIATE_TOP.has(segs[0]));

  // Check if current path is inquiry page, collaboration pages, or landing page
  const isInquiryPage = isAffiliatePath ||
                        location.pathname === '/loading' ||
                        location.pathname === '/thank-you' ||
                        location.pathname === '/price-negotiation' ||
                        location.pathname.startsWith('/collaborations-creator/') ||
                        location.pathname.includes('/offer') ||
                        location.pathname.startsWith('/batch-proposal/') ||
                        location.pathname.startsWith('/batch-creator/') ||
                        location.pathname === '/signup' ||
                        location.pathname === '/signin' ||
                        location.pathname === '/verify-email' ||
                        location.pathname === '/verify-login' ||
                        location.pathname === '/transcript' ||
                        location.pathname.startsWith('/inquiry/') ||
                        location.pathname === '/unsubscribe';

  // Check if we're on home pages (company or creator landing) or privacy/terms
  const isHomePage = location.pathname === '/company' || location.pathname === '/creator' || location.pathname === '/privacy-policy' || location.pathname === '/privacy' || location.pathname === '/terms';

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (prefersDark) {
      setTheme('dark');
    }
  }, []);

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <div className="app">
      {/* Show HomeNavbar for home pages */}
      {isHomePage && <HomeNavbar />}

      {/* Show regular Tabs for non-home, non-inquiry pages */}
      {!isInquiryPage && !isHomePage && (
        <header className="app-header">
          <Tabs />
        </header>
      )}
      <main className={isInquiryPage || isHomePage ? "" : "app-content"}>
        <Routes>
          <Route path="/" element={<Navigate to="/company" replace />} />
          <Route path="/company" element={<LandingPage />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/verify-login" element={<LoginVerification />} />
          <Route path="/find" element={<PrivateRoute><Find /></PrivateRoute>} />
          <Route path="/find/keywords" element={<PrivateRoute><Find /></PrivateRoute>} />
          <Route path="/find/automation" element={<PrivateRoute><Find /></PrivateRoute>} />
          <Route path="/find/groups" element={<PrivateRoute><Find /></PrivateRoute>} />
          <Route path="/find/relevance" element={<PrivateRoute><Find /></PrivateRoute>} />
          <Route path="/find/similar" element={<PrivateRoute><Find /></PrivateRoute>} />
          <Route path="/creators" element={<PrivateRoute><Creators /></PrivateRoute>} />
          <Route path="/creators/tiktok" element={<PrivateRoute><Creators /></PrivateRoute>} />
          <Route path="/creators/instagram" element={<PrivateRoute><Creators /></PrivateRoute>} />
          <Route path="/creators/youtube" element={<PrivateRoute><Creators /></PrivateRoute>} />
          <Route path="/creators/:platform/:username" element={<PrivateRoute><CreatorDetail /></PrivateRoute>} />
          <Route path="/creators/:platform/:username/offer" element={<CreatorOffer />} />
          <Route path="/creators/:platform/:username/manual-offer" element={<CreatorOffer />} />
          <Route path="/outreach" element={<PrivateRoute><Outreach /></PrivateRoute>} />
          <Route path="/outreach/automation" element={<PrivateRoute><Outreach /></PrivateRoute>} />
          <Route path="/outreach/responses" element={<PrivateRoute><Outreach /></PrivateRoute>} />
          <Route path="/outreach/template" element={<PrivateRoute><Outreach /></PrivateRoute>} />
          <Route path="/outreach/sent-emails" element={<PrivateRoute><Outreach /></PrivateRoute>} />
          <Route path="/outreach/unread-emails" element={<PrivateRoute><Outreach /></PrivateRoute>} />
          <Route path="/outreach/inquiry" element={<PrivateRoute><Outreach /></PrivateRoute>} />
          <Route path="/outreach/logs/:ruleId" element={<PrivateRoute><OutreachLogs /></PrivateRoute>} />
          <Route path="/creator" element={<CreatorsLanding />} />
          <Route path="/loading" element={<LoadingPage />} />
          <Route path="/price-negotiation" element={<PriceNegotiation />} />
          <Route path="/collaborations" element={<PrivateRoute><Collaborations /></PrivateRoute>} />
          <Route path="/collaborations/:id" element={<PrivateRoute><CollaborationDetail /></PrivateRoute>} />
          <Route path="/collaboration-workflow/:collaborationId" element={<Navigate to={location.pathname.replace('/collaboration-workflow/', '/collaborations-creator/')} replace />} />
          <Route path="/collaborations-creator/:collaborationId" element={<CollaborationCreator />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/stats" element={<PrivateRoute><Stats /></PrivateRoute>} />
          <Route path="/email" element={<PrivateRoute><Email /></PrivateRoute>} />
          <Route path="/paid-ads" element={<PrivateRoute><PaidAds /></PrivateRoute>} />
          <Route path="/stripe" element={<PrivateRoute><Stripe /></PrivateRoute>} />
          <Route path="/batch" element={<PrivateRoute><Batch /></PrivateRoute>} />
          <Route path="/batch/manual-register" element={<PrivateRoute><ManualBatchRegistration /></PrivateRoute>} />
          <Route path="/batch-proposal/:batchId" element={<BatchProposal />} />
          <Route path="/batch-creator/:batchId" element={<BatchCreator />} />
          <Route path="/batch/:batchId" element={<PrivateRoute><BatchDetail /></PrivateRoute>} />
          <Route path="/transcript" element={<Transcript />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/inquiry/:brand" element={<CollaborationInquiry />} />
          {/* Combined overview of all affiliates (admin) */}
          <Route path="/affiliates" element={<AffiliateOverview />} />
          {/* Per-creator commission vs views by week/month/all time. Declared before the
              /:route/:tab catch-all, which would otherwise read it as route "affiliates". */}
          <Route path="/affiliates/analytics" element={<AffiliateAnalytics />} />
          {/* Recruiting outreach log. Reads the recruiting server through the
              /recruit proxy; lives here so it shares this app's shell, styling
              and password with /affiliates. Path is /sent-emails because
              /recruit is claimed by the proxy Function. */}
          <Route path="/sent-emails" element={<SentEmails />} />
          {/* Draft review queue for Heeyoon: scheduler uploads + Slack DM videos.
              Password checked by the backend (/api/draft-review). Full-screen like /affiliates
              (not in NON_AFFILIATE_TOP), and declared before the /:route catch-all. */}
          <Route path="/draft-review" element={<DraftReview />} />
          {/* Creator-facing scene-by-scene feedback on one draft, linked from the review DM.
              No password: the token is the key (/api/draft-feedback/:token). Full-screen like
              /draft-review, and declared before the /:route/:tab catch-all. */}
          <Route path="/feedback/:token" element={<FeedbackReport />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          {/* Per-affiliate dashboards — DB 정본(config 폐지). route는 URL에서 useParams로 읽고,
              AffiliateStats가 GET /affiliate-meta/:route로 handle/promoCode/searchKeyword/password를
              받는다. 대소문자는 :route 파라미터로 그대로 넘어와 컴포넌트가 toLowerCase() 처리(옛
              lower/upper 이중등록과 동등). 하위탭 /{route}/payouts는 /:route/:tab이 커버.
              ⚠️ catch-all이므로 반드시 다른 모든 명시 라우트보다 뒤(여기)에 둔다. */}
          <Route path="/:route" element={<AffiliateStats />} />
          <Route path="/:route/:tab" element={<AffiliateStats />} />
        </Routes>
      </main>
      {/* Show Footer for home pages */}
      {isHomePage && <Footer />}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;